// generateTicketPDF.js
// Requires: npm i jspdf
// Usage:
//   import { generateTicketPDF } from "./generateTicketPDF";
//   await generateTicketPDF(booking, profile);

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });

// Fetch /logo.png once and convert to base64 for jsPDF
const fetchLogoBase64 = async () => {
  try {
    const res  = await fetch("/logo.png");
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result); // "data:image/png;base64,..."
      reader.onerror  = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null; // logo unavailable — fall back gracefully
  }
};

export const generateTicketPDF = async (booking, profile) => {
  const [{ jsPDF }, logoBase64] = await Promise.all([
    import("jspdf"),
    fetchLogoBase64(),
  ]);

  const doc      = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const W        = 210;
  const H        = 297;
  const tickets  = booking.ticketIds  || [];
  const campaign = booking.campaignId || {};

  // ── Palette ────────────────────────────────────────────────────────────────
  const GOLD   = [201, 168,  76];
  const GOLDD  = [168, 134,  58];
  const GOLDL  = [232, 201, 106];
  const CREAM  = [255, 253, 245];
  const CREAMM = [255, 241, 204];
  const CREAMB = [240, 217, 140];
  const TEXT   = [ 92,  74,  30];
  const TEXTL  = [184, 154,  90];
  const WHITE  = [255, 255, 255];
  const DARK   = [ 36,  22,   4];

  const sf = (...c) => doc.setFillColor(...c);
  const ss = (...c) => doc.setDrawColor(...c);
  const st = (...c) => doc.setTextColor(...c);
  const rr = (x, y, w, h, r, mode = "F") => doc.roundedRect(x, y, w, h, r, r, mode);

  const dashed = (x1, y1, x2, y2) => {
    doc.setLineDashPattern([1.5, 1.5], 0);
    ss(...CREAMB);
    doc.setLineWidth(0.3);
    doc.line(x1, y1, x2, y2);
    doc.setLineDashPattern([], 0);
  };

  const barcodeStrip = (x, y, w) => {
    const n = 30, bw = w / n;
    for (let i = 0; i < n; i++) {
      sf(...(i % 4 === 0 ? GOLDD : CREAMB));
      doc.rect(x + i * bw, y, bw * 0.65, i % 5 === 0 ? 5 : i % 3 === 0 ? 4 : 3, "F");
    }
  };

  // ── Embed logo image (or fallback diamond mark) ───────────────────────────
  // LW / LH = logo width / height in mm as rendered in the header
  const LW = 22, LH = 22;

  const placeLogo = (x, y, w, h) => {
    if (logoBase64) {
      doc.addImage(logoBase64, "PNG", x, y, w, h);
    } else {
      // Fallback: simple diamond mark if logo fetch failed
      const cx = x + w / 2, cy = y + h / 2, s = Math.min(w, h) / 2;
      sf(...GOLDL);
      doc.triangle(cx, cy - s, cx + s, cy, cx, cy + s, "F");
      doc.triangle(cx, cy - s, cx - s, cy, cx, cy + s, "F");
      sf(...GOLDD);
      const is = s * 0.52;
      doc.triangle(cx, cy - is, cx + is, cy, cx, cy + is, "F");
      doc.triangle(cx, cy - is, cx - is, cy, cx, cy + is, "F");
      sf(...WHITE);
      doc.circle(cx, cy, s * 0.18, "F");
    }
  };

  const invNo = "INV-" + (booking._id || "").slice(-8).toUpperCase();

  // ═══════════════════════════════════════════════════════════════════════════
  //  PAGE BACKGROUND
  // ═══════════════════════════════════════════════════════════════════════════
  sf(...CREAM);
  doc.rect(0, 0, W, H, "F");

  // ═══════════════════════════════════════════════════════════════════════════
  //  HEADER  (0 → 50mm)
  // ═══════════════════════════════════════════════════════════════════════════

  // Dark base
  sf(...DARK);
  doc.rect(0, 0, W, 50, "F");

  // Warm gold fade in lower portion of band
  sf(...GOLDD);
  doc.rect(0, 36, W, 14, "F");
  sf(...GOLD);
  doc.rect(0, 44, W, 6, "F");

  // Bright accent rule at base of header
  sf(...GOLDL);
  doc.rect(0, 49.2, W, 0.8, "F");

  // Corner decorative triangles
  sf(...GOLDD);
  doc.triangle(0, 0, 44, 0, 0, 44, "F");
  doc.triangle(W, 0, W - 44, 0, W, 44, "F");
  sf(...GOLDL);
  doc.triangle(0, 0, 24, 0, 0, 24, "F");
  doc.triangle(W, 0, W - 24, 0, W, 24, "F");

  // Subtle horizontal rule lines framing the brand name area
  ss(255, 240, 160);
  doc.setLineWidth(0.15);
  doc.line(52, 13, W - 52, 13);
  doc.line(52, 39, W - 52, 39);

  // ── LOGO  left side ───────────────────────────────────────────────────────
  // Logo is placed left-of-centre, vertically centred in the header
  const LOGO_X = 8, LOGO_Y = 4;
  placeLogo(LOGO_X, LOGO_Y, LW, LH);

  // ── LOGO  right side (mirror) ─────────────────────────────────────────────
  placeLogo(W - LOGO_X - LW, LOGO_Y, LW, LH);

  // ── Brand name ────────────────────────────────────────────────────────────
  st(...WHITE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(23);
  doc.text("DreamToday", W / 2, 22, { align: "center" });

  // Tagline
  st(...GOLDL);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6);
  doc.setCharSpace(4);
  doc.setCharSpace(0);

  // Ornamental divider under tagline
  const divOff = 38;
  ss(...GOLDL);
  doc.setLineWidth(0.35);
  doc.line(W / 2 - divOff, 32.5, W / 2 - 7, 32.5);
  doc.line(W / 2 + 7,      32.5, W / 2 + divOff, 32.5);
  // Small diamond centrepiece
  sf(...GOLDL);
  doc.triangle(W / 2, 31,    W / 2 + 2.5, 32.5, W / 2, 34,    "F");
  doc.triangle(W / 2, 31,    W / 2 - 2.5, 32.5, W / 2, 34,    "F");

  // Website URL
  st(...CREAMB);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(5.5);
  doc.setCharSpace(1);
  doc.text("www.dreamtoday.in", W / 2, 38.5, { align: "center" });
  doc.setCharSpace(0);

  // ── Invoice meta (bottom-right inside header) ─────────────────────────────
  st(...CREAMM);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(6);
  [
    // `Invoice #: ${invNo}`,
    // `Date: ${formatDate(booking.bookingDate)}`,
    // `Tickets: ${tickets.length}`,
    // `Amount: Rs.${booking.totalAmount}`,
  ].forEach((line, i) => doc.text(line, W - 8, 8 + i * 4.2, { align: "right" }));

  // ── HEADER ends ───────────────────────────────────────────────────────────

  let cy = 60;
  const ML = 14;
  const cw = (W - ML * 2 - 6) / 2;

  // ═══════════════════════════════════════════════════════════════════════════
  //  BILLED TO  /  CAMPAIGN DETAILS
  // ═══════════════════════════════════════════════════════════════════════════
  [
    [ML, "BILLED TO", [
      ["Name",    profile?.name  || sessionStorage.getItem("userName")  || "—"],
      ["Phone",   profile?.phone || sessionStorage.getItem("userPhone") || "—"],
      ["Email",   profile?.email || sessionStorage.getItem("userEmail") || "—"],
      ["User ID", (sessionStorage.getItem("userId") || "—").slice(-10)],
    ]],
    [ML + cw + 6, "CAMPAIGN DETAILS", [
      ["Title",    booking.campaignTitle || "—"],
      ["Category", (campaign.campaignCategory || "Campaign").toUpperCase()],
      ["Start",    formatDate(booking.campaignStartDate)],
      ["End",      formatDate(booking.campaignEndDate)],
    ]],
  ].forEach(([cx, heading, rows]) => {
    sf(...CREAMM); rr(cx, cy, cw, 44, 4);
    ss(...CREAMB); doc.setLineWidth(0.35); rr(cx, cy, cw, 44, 4, "S");

    st(...GOLDD);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setCharSpace(2);
    doc.text(heading, cx + 5, cy + 7);
    doc.setCharSpace(0);

    ss(...GOLDL);
    doc.setLineWidth(0.4);
    doc.line(cx + 5, cy + 9.5, cx + cw - 5, cy + 9.5);

    rows.forEach(([lbl, val], i) => {
      const ry = cy + 15 + i * 7;
      st(...TEXTL);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(6.5);
      doc.text(lbl, cx + 5, ry);
      st(...TEXT);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      doc.text(doc.splitTextToSize(String(val), cw - 30)[0] || "—", cx + 28, ry);
    });
  });
  cy += 52;

  // ═══════════════════════════════════════════════════════════════════════════
  //  PRIZES
  // ═══════════════════════════════════════════════════════════════════════════
  if (campaign.prizes?.length) {
    sf(...GOLDD); rr(ML, cy, W - ML * 2, 9, 2);
    st(...WHITE);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setCharSpace(2);
    doc.text("PRIZES", ML + 5, cy + 6);
    doc.setCharSpace(0);
    cy += 13;

    const pcols  = Math.min(campaign.prizes.length, 3);
    const pw     = (W - ML * 2 - (pcols - 1) * 4) / pcols;
    const medals = ["1st", "2nd", "3rd", "4th", "5th", "6th"];

    campaign.prizes.slice(0, 6).forEach((p, i) => {
      if (i > 0 && i % pcols === 0) cy += 16;
      const px = ML + (i % pcols) * (pw + 4);
      sf(...CREAMM); rr(px, cy, pw, 13, 3);
      ss(...CREAMB); rr(px, cy, pw, 13, 3, "S");
      st(...GOLDD);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(6.5);
      doc.text(medals[i] || `${i + 1}th`, px + 4, cy + 6);
      st(...TEXT);
      doc.setFontSize(6.5);
      doc.text(doc.splitTextToSize(p.prizeName, pw - 20)[0], px + 18, cy + 6);
      if (p.description) {
        st(...TEXTL);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(5.5);
        doc.text(doc.splitTextToSize(p.description, pw - 20)[0], px + 18, cy + 10.5);
      }
    });
    cy += 20;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  //  TICKETS SECTION HEADER
  // ═══════════════════════════════════════════════════════════════════════════
  sf(...GOLD); rr(ML, cy, W - ML * 2, 10, 2);
  st(...WHITE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setCharSpace(2);
  doc.text(`YOUR TICKETS  (${tickets.length})`, ML + 5, cy + 6.8);
  doc.setCharSpace(0);
  cy += 15;

  // ═══════════════════════════════════════════════════════════════════════════
  //  TICKET STUBS  — 2 per row, multi-page aware
  // ═══════════════════════════════════════════════════════════════════════════
  const TW      = (W - ML * 2 - 5) / 2;
  const TH      = 36;
  const TGAP    = 5;
  const ROWS_PP = Math.floor((H - cy - 22) / (TH + TGAP));

  tickets.forEach((tid, idx) => {
    const col = idx % 2;
    const row = Math.floor(idx / 2);

    // New continuation page
    if (row > 0 && row % ROWS_PP === 0 && col === 0) {
      doc.addPage();
      sf(...CREAM); doc.rect(0, 0, W, H, "F");

      // Mini header on continuation pages
      sf(...DARK);  doc.rect(0, 0, W, 15, "F");
      sf(...GOLD);  doc.rect(0, 13, W, 2, "F");
      sf(...GOLDL); doc.rect(0, 14.8, W, 0.5, "F");

      // Logo on continuation pages (small)
      placeLogo(6, 1.5, 11, 11);
      placeLogo(W - 17, 1.5, 11, 11);

      st(...WHITE);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.text("DreamToday", W / 2, 7.5, { align: "center" });

      st(...GOLDL);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(5.5);
      doc.setCharSpace(1.2);
      doc.text(
        "TICKETS CONTINUED — " + (booking.campaignTitle || "").toUpperCase().slice(0, 38),
        W / 2, 12, { align: "center" }
      );
      doc.setCharSpace(0);

      cy = 23;
    }

    const localRow = row % ROWS_PP;
    const tx = ML + col * (TW + 5);
    const ty = cy + localRow * (TH + TGAP);

    // Card
    sf(...WHITE); rr(tx, ty, TW, TH, 4);
    sf(...GOLD);  doc.roundedRect(tx, ty, TW, 2.8, 1, 1, "F");
    ss(...CREAMB); doc.setLineWidth(0.35); rr(tx, ty, TW, TH, 4, "S");

    // Notches
    sf(...CREAM);
    doc.circle(tx,      ty + TH / 2, 2.8, "F");
    doc.circle(tx + TW, ty + TH / 2, 2.8, "F");

    // Number badge
    sf(...CREAMM); rr(tx + 4, ty + 5, 16, 17, 3);
    ss(...CREAMB); rr(tx + 4, ty + 5, 16, 17, 3, "S");
    st(...GOLDD);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.text(String(idx + 1).padStart(2, "0"), tx + 12, ty + 16, { align: "center" });

    // Ticket ID
    st(...TEXTL); doc.setFont("helvetica", "normal"); doc.setFontSize(6);
    doc.text("TICKET ID", tx + 24, ty + 9.5);
    st(...TEXT); doc.setFont("helvetica", "bold"); doc.setFontSize(7.5);
    const ts = String(tid);
    doc.text(ts.length > 20 ? ts.slice(0, 20) + "..." : ts, tx + 24, ty + 16);

    // Valid till
    st(...TEXTL); doc.setFont("helvetica", "normal"); doc.setFontSize(6);
    doc.text("VALID TILL", tx + TW - 4, ty + 9.5, { align: "right" });
    st(...GOLDD); doc.setFont("helvetica", "bold"); doc.setFontSize(7);
    doc.text(formatDate(booking.campaignEndDate), tx + TW - 4, ty + 16, { align: "right" });

    dashed(tx + 4, ty + 23, tx + TW - 4, ty + 23);
    barcodeStrip(tx + 4, ty + 26, TW - 8);
  });

  const usedRows = Math.min(Math.ceil(tickets.length / 2), ROWS_PP);
  cy += usedRows * (TH + TGAP) + 6;

  // ═══════════════════════════════════════════════════════════════════════════
  //  SUMMARY STRIP
  // ═══════════════════════════════════════════════════════════════════════════
  if (cy + 34 > H - 22) {
    doc.addPage();
    sf(...CREAM); doc.rect(0, 0, W, H, "F");
    cy = 20;
  }

  sf(...CREAMM); rr(ML, cy, W - ML * 2, 30, 4);
  ss(...CREAMB); doc.setLineWidth(0.35); rr(ML, cy, W - ML * 2, 30, 4, "S");

  const summItems = [
    ["Unit Price", `Rs.${Math.round(booking.totalAmount / (tickets.length || 1))} x ${tickets.length}`],
    ["Total Paid", `Rs.${booking.totalAmount}`],
    ["Status",     "CONFIRMED"],
    ["Booking Ref", (booking._id || "").slice(-10).toUpperCase()],
  ];
  const sw = (W - ML * 2) / summItems.length;

  summItems.forEach(([lbl, val], i) => {
    const sx = ML + i * sw + 6;
    st(...TEXTL); doc.setFont("helvetica", "normal"); doc.setFontSize(6);
    doc.text(lbl, sx, cy + 11);
    st(...(i === 1 ? GOLDD : TEXT));
    doc.setFont("helvetica", "bold");
    doc.setFontSize(i === 1 ? 9.5 : 7.5);
    doc.text(String(val), sx, cy + 20);
    if (i < summItems.length - 1) {
      ss(...CREAMB); doc.setLineWidth(0.3);
      doc.line(ML + (i + 1) * sw, cy + 4, ML + (i + 1) * sw, cy + 26);
    }
  });

  // ═══════════════════════════════════════════════════════════════════════════
  //  FOOTER  (bottom 20mm)
  // ═══════════════════════════════════════════════════════════════════════════
  const FY = H - 20;

  // Background: dark → gold
  sf(...DARK);  doc.rect(0, FY, W, 20, "F");
  sf(...GOLDD); doc.rect(0, FY + 12, W, 8, "F");

  // Top accent rule
  sf(...GOLDL); doc.rect(0, FY, W, 1, "F");

  // Subtle inner divider
  ss(...GOLDL); doc.setLineWidth(0.2);
  doc.line(ML, FY + 12, W - ML, FY + 12);

  // Logo in footer (left & right, small)
  placeLogo(ML - 2, FY + 1.5, 9, 9);
  placeLogo(W - ML - 7, FY + 1.5, 9, 9);

  // Brand name
  st(...WHITE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("DreamToday", W / 2, FY + 8, { align: "center" });

  // Disclaimer
  st(232, 201, 106);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(5.5);
  doc.setCharSpace(0.3);
  doc.text(
    "This is an official DreamToday ticket invoice. Please retain for verification purposes.",
    W / 2, FY + 14.5, { align: "center" }
  );
  doc.setCharSpace(0);

  // Website + copyright
  st(...CREAMB);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(5.5);
  doc.text("www.dreamtoday.in  \u00B7  \u00A9 DreamToday", W / 2, FY + 17.5, { align: "center" });

  // ═══════════════════════════════════════════════════════════════════════════
  //  DIAGONAL WATERMARK
  // ═══════════════════════════════════════════════════════════════════════════
  doc.saveGraphicsState();
  doc.setGState(new doc.GState({ opacity: 0.035 }));
  st(...GOLDD);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(54);
  doc.text("DreamToday", W / 2, 165, { align: "center", angle: 45 });
  doc.restoreGraphicsState();

  // ── Save ───────────────────────────────────────────────────────────────────
  const safeName = (booking.campaignTitle || "campaign")
    .replace(/[^a-z0-9]/gi, "_")
    .toLowerCase();
  doc.save(`dreamtoday_tickets_${safeName}_${invNo}.pdf`);
};